



import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Mixins } from 'vue-mixin-decorator';
import { mapGetters, mapActions } from 'vuex';

import dayjs from 'dayjs';

import Btn, { BtnColor, BtnWidth } from '@/shared/components/btn.component.vue';
import Datepicker from '@/shared/components/datepicker/index.vue';
import Dropdown from '@/shared/components/Dropdown.component.vue';
import DropUp, { DropUpColorHover } from '@/shared/components/DropUp.component.vue';
import EmptyState from '@/shared/components/empty-state.component.vue';
import ItemThumb from '@/shared/components/item-thumb.component.vue';
import Pagination from '@/shared/components/Pagination/Pagination.component.vue';
import Search from '@/shared/components/search.component.vue';
import SearchMixin from '@/shared/mixins/search.mixin.ts';
import SpotlightText, { SpotlightTextColor } from '@/shared/components/SpotlightText.component.vue';
import ColumnOrderMixin from '@/shared/mixins/columnOrder.mixin.ts';
import Spinner from '@/shared/components/Spinner.component.vue';
import SyncRow from '@/manager/sync/views/syncDetail/syncRow/syncRow.view.vue';
import TableColumnOrder, {
    TableColumnOrderColor,
} from '@/shared/components/TableColumnOrder.component.vue';
import TopBar from '@/shared/components/TopBar.component.vue';
import WithPagination from '@/shared/mixins/withPagination.mixin.ts';
import { Operation, Income, Expense } from '@/manager/sync/operation.model';
import { Product } from '@/manager/product/models/product.model';
import { DelegationInterface } from '@/shared/interfaces/delegation.interface';
import { OperationTypeInterface } from '@/manager/sync/operationType.interface';
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
import KPIModal from '@/manager/sync/views/syncDetail/kpiModal/kpiModal.view.vue';
import NotificationMessage from '@/shared/mixins/notificationMessage.mixin';

import { SyncService } from '@/manager/sync/sync.service';

interface MixinInterface extends WithPagination, SearchMixin, ColumnOrderMixin, NotificationMessage {}

export enum ListActions {
    ImportCsv = 'import-csv',
}

export enum CellLineColorEnum {
    Primary = 'cell-line--primary',
    Secondary = 'cell-line--secondary',
}

@Component({
    components: {
        Btn,
        Datepicker,
        Dropdown,
        DropUp,
        EmptyState,
        Icon,
        ItemThumb,
        KPIModal,
        Pagination,
        Search,
        Spinner,
        SpotlightText,
        SyncRow,
        TableColumnOrder,
        TopBar,
    },
    computed: {
        ...mapGetters({
            operationList: 'getOperationList',
            productList: 'getProductList',
            delegationList: 'getDelegationList',
            operationTypeList: 'getOperationTypeList',
        }),
    },
    methods: {
        ...mapActions({
            fetchProductList: 'fetchProductList',
            fetchDelegationList: 'fetchDelegationList',
            fetchOperationTypeList: 'fetchOperationTypeList',
        }),
    },
})
export default class SyncDetail extends Mixins<MixinInterface> (
    WithPagination,
    SearchMixin,
    ColumnOrderMixin,
    NotificationMessage,
) {

    private operationList!: Operation[];
    private cleanSearch = false;
    private operationListLoaded = false;
    private productList!: Product[];
    private delegationList!: DelegationInterface[];
    private operationTypeList!: OperationTypeInterface[];
    private fetchProductList!: any;
    private fetchDelegationList!: any;
    private fetchOperationTypeList!: any;
    private btnColor = BtnColor;
    private btnWidth = BtnWidth;
    private dropUpColorHover = DropUpColorHover;
    private spotlightTextColor = SpotlightTextColor;
    private dateFrom = '';
    private dateTo = '';
    private dateFromPlaceholder = '';
    private dateToPlaceholder = '';
    private limitDate = '';
    private type = '';
    private iconEnum = IconEnum;
    private kpiModalOpened = false;
    private cellLineColor: CellLineColorEnum = CellLineColorEnum.Primary;
    private dropdownOptions = [
        { id: 1, name: 'Ingresos' },
        { id: 2, name: 'Gastos' },
        { id: 3, name: 'Todos los movimientos' },
    ];
    private optionSelected = this.dropdownOptions[2];
    private tableColumnOrderColor = TableColumnOrderColor;

    private created() {
        this.initializePagination();
        this.getQueryStringDates();
        this.getQueryStringType();
        setTimeout(() => {
            this.requestShowSpinner();
            this.fetchSelectorLists();
            this.fetchFirstPageList();
        }, 0);
    }

    @Watch('$route.query')
    private watchRouteQuery(newVal: any, oldVal: any) {
        this.getQueryStringDates();
        this.getQueryStringType();
        this.fetchFirstPageList();
    }

    @Watch('optionSelected')
    private watchOptionSelected(newVal: any, oldVal: any) {
        if (newVal.id === 1) {
            this.cellLineColor = CellLineColorEnum.Primary;
            this.type = newVal.id;
        } else if (newVal.id === 2) {
            this.cellLineColor = CellLineColorEnum.Secondary;
            this.type = newVal.id;
        } else {
            this.cellLineColor = CellLineColorEnum.Primary;
            this.type = '';
        }
        this.pushQueryStrings();
    }

    get numberOfMovements() {
        const transactions = this.operationList.length;
        let result = `${transactions} nuevos movimientos`;
        if (transactions === 1) {
            result = `${transactions} nuevo movimiento`;
        }
        return result;
    }

    get dropUpOption() {
        return [
            { value: ListActions.ImportCsv, name: 'Importar xls' },
        ];
    }

    get parsedProductListOptions() {
        return this.productList.map(
            (product: any) => {
                const option = {
                    value: product.id,
                    label: product.name,
                };
                return option;
            },
        );
    }

    get parsedDelegationOptions() {
        return this.delegationList.map(
            (delegation: any) => {
                const option = {
                    value: delegation.id,
                    label: `${delegation.name}`,
                };
                return option;
            },
        );
    }

    get parsedOperationTypeIncomeListOptions() {
        return this.operationTypeList.filter(
            (operationType: any) => operationType.mode === Income,
        ).map(
            (operationType: any) => {
                const option = {
                    value: operationType.id,
                    label: `${operationType.name}`,
                };
                return option;
            },
        );
    }

    get parsedOperationTypeExpenseListOptions() {
        return this.operationTypeList.filter(
            (operationType: any) => operationType.mode === Expense,
        ).map(
            (operationType: any) => {
                const option = {
                    value: operationType.id,
                    label: `${operationType.name}`,
                };
                return option;
            },
        );
    }

    get showTable() {
        return (this.operationListLoaded && !this.spinner.show && !this.showEmptyState);
    }

    get showEmptyState() {
        return (this.operationListLoaded && !this.spinner.show && this.operationList.length === 0);
    }

    get showPagination() {
        return this.operationListLoaded && !this.spinner.show && this.operationList.length > 0;
    }

    get dateFromRequest() {
        let date = this.dateFromPlaceholder;
        if (this.dateFrom) {
            date = this.dateFrom;
        }
        return date;
    }

    get dateToRequest() {
        let date = this.dateToPlaceholder;
        if (this.dateTo) {
            date = this.dateTo;
        }
        return date;
    }

    get searchPlaceholder() {
        let placeholder = 'Busca un concepto';
        if ( this.type.toString() === '1' ) {
            placeholder = 'Busca un ingreso';
        } else if ( this.type.toString() === '2') {
            placeholder = 'Busca un gasto';
        }
        return placeholder;
    }

    get openModal() {
        return (this.kpiModalOpened);
    }

    get tableColumnOrderColorSelected() {
        let color = this.tableColumnOrderColor.Primary;
        if ( this.type.toString() === '2' ) {
            color = this.tableColumnOrderColor.Secondary;
        }
        return color;
    }

    get isFirstColumnOrderActive() {
        let result = false;
        if (this.composeOrdering() === '') {
            result = true;
        }
        return result;
    }

    private fetchSelectorLists() {
        this.fetchProductList();
        this.fetchDelegationList();
        this.fetchOperationTypeList();
    }

    private setKpiModalOpened( value: boolean) {
        this.kpiModalOpened = value;
    }

    private dateRangeSelected(from: string, to: string) {
        this.dateTo = to;
        this.dateFrom = from;
        this.pushQueryStrings();
    }

    private pushQueryStrings() {
        const type = this.type;
        const to = this.dateTo;
        const from = this.dateFrom;
        this.$router.push({ name: this.$route.name , query: { from, to, type }});
    }

    // tslint:disable-next-line:member-ordering
    public fetchList() {
        this.changeSpinnerVisibility( true );
        this.fetchOperationList(
            (res: any) => {
                this.onFetchSuccess(res);
                this.operationListLoaded = true;
                this.changeSpinnerVisibility( false );
            },
        );
    }

    // tslint:disable-next-line:member-ordering
    public searchCallback() {
        this.fetchFirstPageList();
    }

    private getQueryStringDates() {
        const from = this.$route.query.from;
        const to = this.$route.query.to;
        const today = dayjs(new Date(), {locale: 'es'});

        if ( from ) {
            this.dateFrom = from;
        } else {
            const firstDayOfYear = today.set('month', 0).set('date', 1);
            this.dateFromPlaceholder = firstDayOfYear.toISOString();
        }

        if ( to ) {
            this.dateTo = to;
        } else {
            this.dateToPlaceholder = today.toISOString();
        }

        if ( this.dateToPlaceholder ) {
            this.limitDate = dayjs(this.dateToPlaceholder, { locale: 'es' }).add(1, 'day').toISOString();
        }

    }

    private getQueryStringType() {
        const urlType = this.$route.query.type;
        if (urlType) {
            this.type = urlType;
        }
        const newOption = this.dropdownOptions.find(
            (option) => {
                return option.id.toString() === this.type;
            },
        );
        if (newOption) {
            this.optionSelected = newOption;
        }
    }

    private fetchOperationList(callback: any = () => { const doNothing = 1; }) {
        this.$store.dispatch(
            'fetchOperationList',
            {
                offset: this.offset,
                limit: this.limit,
                type: this.type,
                // filter: this.filters,
                ordering: this.composeOrdering(),
                search: this.searchString,
                from: this.dateFromRequest,
                to: this.dateToRequest,
            },
        )
            .then((res: any) => {
                this.changeSpinnerVisibility( false );
                if ( callback ) {
                    callback(res);
                }
            })
            .catch((err) => {
                this.changeSpinnerVisibility( false );
            });
    }

    private initializePagination() {
        this.itemsPerPage = 100;
    }

    private fileToImportChange(e: any) {
        this.changeSpinnerVisibility( true );
        const file = e.target.files[0];
        if ( file ) {
            SyncService.importCsv(file)
                .then(
                    (res) => {
                        this.showNotification('Importación realizada con éxito');
                        this.fetchFirstPageList();
                    },
                )
                .catch(
                    (err) => {
                        this.showNotification(
                            'Error en la importación',
                            this.spotlightTextColor.Error,
                        );
                        this.fetchFirstPageList();
                    },
                );
            (this.$refs.fileToImport as HTMLInputElement).value = '';
        }
    }

    private handleComeBack() {
        this.cleanSearch = !this.cleanSearch;
        this.dateFrom = '';
        this.dateTo = '';
        this.type = '';
        if (this.searchString === '') {
            this.fetchFirstPageList();
        } else {
            this.searchString = '';
        }
    }

    // tslint:disable-next-line:member-ordering
    public columnOrderFetchList() {
        this.requestShowSpinner();
        this.fetchOperationList(
            (res: any) => {
                this.columnOrderOnFetchSuccess();
                this.operationListLoaded = true;
                this.changeSpinnerVisibility( false );
            },
        );
    }

    private dropUpOptionSelected(option: any) {
        switch (option.value) {
            case ListActions.ImportCsv:

                const inputFile = (this.$refs.fileToImport as HTMLInputElement);
                inputFile.click();
                break;
        }
    }

}
